import { addHours } from 'date-fns'
import { format } from 'date-fns-tz'
import { sumBy } from 'lodash-es'

import {
  convertTemplateToText,
  convertTokenToText,
} from '@lyrahealth-inc/shared-app-logic/src/features/messaging/tokens'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { ConversationObject } from '@lyrahealth-inc/shared-app-logic/src/models/conversation/Conversation'
import {
  CONVERSATION_TYPE,
  ConversationMessages,
  isPatientSideAuthorType,
} from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { MessageToken } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Tokens'
import { decodeHtmlEntities } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

export const getDerivedProperties = (showOverdueMessageFlag: boolean = false) => {
  return showOverdueMessageFlag ? 'overdue' : undefined
}

export const getConversationsUnreadCount = (conversations: ConversationObject[] | undefined) => {
  return (
    sumBy(
      conversations,
      (conversation) => conversation?.conversation_attributes?.unread_provider_messages_count ?? 0,
    ) || 0
  )
}

/**
 * Converts a template with tokens to text by replacing tokens with actual values
 * @param templateText - The template text containing tokens
 * @param clientDetails - Client information for token replacement
 * @param nextClientAppointment - Next appointment information for token replacement
 * @returns The converted text with tokens replaced by actual values
 */
export const convertTemplateWithTokens = (
  templateText: string,
  clientDetails: ClientObject | undefined,
  nextClientAppointment: Appointment | undefined | null,
): string => {
  if (!templateText) {
    return ''
  }
  const dateTime72HoursFromNow = addHours(new Date(), 72)

  const getValidTimeZone = (timeZone: string | undefined | null): string => {
    if (!timeZone || timeZone.trim() === '') {
      return 'America/Los_Angeles'
    }
    return timeZone
  }
  const tokenPattern = /<token\s+name="[^"]*"\s+label="[^"]*"><\/token>/
  const decodedText = decodeHtmlEntities(templateText)
  if (tokenPattern.test(decodedText)) {
    return convertTokenToText(decodedText, {
      [MessageToken.CLIENT_FIRST_NAME]:
        clientDetails?.first_name ?? '<span class="error-text">[ERROR: getting client name]</span>',
      [MessageToken.NEXT_SESSION_DATETIME]: nextClientAppointment
        ? format(
            new Date(`${nextClientAppointment?.startDate} ${nextClientAppointment?.startTime}`),
            'eeee, MMMM d hh:mm a zzz',
            {
              timeZone: getValidTimeZone(nextClientAppointment.timeZone as string),
            },
          )
        : '<span class="error-text">[ERROR: getting client session date & time]</span>',
      [MessageToken.BOOKING_LINK]: '',
      [MessageToken.DATE_TIME_72_HOURS]: format(dateTime72HoursFromNow, 'eeee, MMMM d hh:mm a zzz', {
        timeZone: getValidTimeZone(clientDetails?.time_zone_full),
      }),
    })
  } else {
    return convertTemplateToText(decodedText, {
      [MessageToken.CLIENT_FIRST_NAME]:
        clientDetails?.first_name ?? '<span class="error-text">[ERROR: getting client name]</span>',
      [MessageToken.NEXT_SESSION_DATETIME]: nextClientAppointment
        ? format(
            new Date(`${nextClientAppointment?.startDate} ${nextClientAppointment?.startTime}`),
            'eeee, MMMM d hh:mm a zzz',
            {
              timeZone: getValidTimeZone(nextClientAppointment.timeZone as string),
            },
          )
        : '<span class="error-text">[ERROR: getting client session date & time]</span>',
      [MessageToken.BOOKING_LINK]: '',
      [MessageToken.DATE_TIME_72_HOURS]: format(dateTime72HoursFromNow, 'eeee, MMMM d hh:mm a zzz', {
        timeZone: getValidTimeZone(clientDetails?.time_zone_full),
      }),
    })
  }
}

/**
 * Checks if a string contains span tags with class "error-text" that have error patterns, i.e. <span class="error-text">[ERROR: ...]</span>
 * @param text - The HTML string to search for error span tags
 * @returns true if span tags with "error-text" class containing error patterns are found, false otherwise
 */
export function hasErrorSpanPattern(text: string): boolean {
  const spanErrorPattern = /<span[^>]*class="error-text"[^>]*>\[ERROR:[^\]]+\]<\/span>/
  return spanErrorPattern.test(text)
}

type MessageSender = {
  authorType?: string
  authorId?: string
}

type ParsedMessageSenderAttributes = {
  author_type?: string
  from?: string
}

export const isMessageFromRecipient = (
  { authorType, authorId }: MessageSender,
  currentProviderId: string | undefined,
  conversationType: CONVERSATION_TYPE | undefined,
): boolean => {
  if (conversationType === CONVERSATION_TYPE.PROVIDER_PROVIDER) {
    // A provider-pair message is outgoing only when its sender explicitly matches the
    // current provider. Unknown senders must not be attributed to the current provider.
    return !authorId || authorId !== currentProviderId
  }
  return isPatientSideAuthorType(authorType)
}

/**
 * Checks whether the conversation contains a message from the selected recipient.
 * Provider-pair conversations identify the recipient by sender id; existing conversation
 * types retain their patient/guardian author-type behavior.
 */
export const hasRecipientMessages = (
  messagesData: ConversationMessages | undefined,
  currentProviderId: string | undefined,
  conversationType: CONVERSATION_TYPE | undefined,
): boolean => {
  if (!messagesData?.conversation_messages) {
    return false
  }

  return messagesData.conversation_messages.some((message) => {
    try {
      const attributes = JSON.parse(message.attributes) as ParsedMessageSenderAttributes
      return (
        isMessageFromRecipient(
          { authorType: attributes.author_type, authorId: attributes.from },
          currentProviderId,
          conversationType,
        ) === true
      )
    } catch {
      return false
    }
  })
}

type ConversationRecipient = {
  conversationType: CONVERSATION_TYPE | undefined
  conversationPatientId: string
  conversationCounterpartProviderId?: string
  selectedClientId: string | null
  clientId?: string
  recipientId?: string
}

export const doesConversationMatchRecipient = ({
  conversationType,
  conversationPatientId,
  conversationCounterpartProviderId,
  selectedClientId,
  clientId,
  recipientId,
}: ConversationRecipient): boolean =>
  conversationType === CONVERSATION_TYPE.PROVIDER_PROVIDER
    ? Boolean(
        selectedClientId &&
        recipientId &&
        conversationPatientId === selectedClientId &&
        conversationCounterpartProviderId === recipientId,
      )
    : conversationPatientId === (recipientId ?? clientId)
